import React, { useState } from 'react';
import { WeeklySession } from '../../types/athlete';
import { X, Check, Star, Clock, FileText } from 'lucide-react';

interface SessionCompletionModalProps {
  session: WeeklySession;
  onComplete: (data: {
    completed: boolean;
    actual_duration_minutes?: number;
    felt_rating?: number;
    completion_notes?: string;
  }) => void;
  onCancel: () => void;
}

const SessionCompletionModal: React.FC<SessionCompletionModalProps> = ({
  session,
  onComplete,
  onCancel,
}) => {
  const isCompleting = !session.completed;
  const [completed, setCompleted] = useState(session.completed || false);
  const [actualDuration, setActualDuration] = useState<number | ''>(
    session.actual_duration_minutes || session.planned_duration_minutes || ''
  );
  const [feltRating, setFeltRating] = useState(session.felt_rating || 3);
  const [notes, setNotes] = useState(session.completion_notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete({
      completed,
      actual_duration_minutes: completed && actualDuration ? Number(actualDuration) : undefined,
      felt_rating: completed ? feltRating : undefined,
      completion_notes: completed && notes ? notes : undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="card-base w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-[#2d3748]">
          <h2 className="text-xl font-semibold">
            {isCompleting ? 'Complete Session' : 'Edit Session'}
          </h2>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-[#2d3748] rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Session Info */}
          <div className="card-base p-3 bg-[#0a0e14]">
            <p className="font-medium">{session.session_type?.name || 'Unknown Session'}</p>
            <p className="text-sm text-muted">
              {session.scheduled_date} • {session.planned_duration_minutes} min planned
            </p>
          </div>

          {/* Mark Complete Toggle */}
          <div className="flex items-center justify-between p-3 bg-[#0a0e14] rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                completed ? 'bg-green-500 border-green-500' : 'border-[#718096]'
              }`}>
                {completed && <Check size={14} className="text-white" />}
              </div>
              <span className="font-medium">Mark as Complete</span>
            </div>
            <button
              type="button"
              onClick={() => setCompleted(!completed)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                completed ? 'bg-green-500' : 'bg-[#2d3748]'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                completed ? 'translate-x-7' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {completed && (
            <>
              {/* Actual Duration */}
              <div>
                <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                  <Clock size={16} className="inline mr-2" />
                  Actual Duration (minutes)
                </label>
                <input
                  type="number"
                  min="1"
                  max="600"
                  value={actualDuration}
                  onChange={(e) => setActualDuration(e.target.value ? Number(e.target.value) : '')}
                  className="w-full"
                  placeholder="Enter actual duration"
                />
              </div>

              {/* Felt Rating */}
              <div>
                <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                  <Star size={16} className="inline mr-2" />
                  How did it feel? (1-5)
                </label>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => setFeltRating(rating)}
                      className={`flex-1 py-2 rounded-lg transition-colors ${
                        feltRating === rating
                          ? 'bg-[#3b82f6] text-white'
                          : 'bg-[#141921] text-[#718096] hover:bg-[#2d3748]'
                      }`}
                    >
                      {rating}
                      <span className="block text-xs opacity-75">
                        {rating === 1 ? 'Terrible' : rating === 2 ? 'Hard' : rating === 3 ? 'Okay' : rating === 4 ? 'Good' : 'Great'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                  <FileText size={16} className="inline mr-2" />
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full h-24 resize-none"
                  placeholder="How did the session go? Any observations?"
                />
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-[#141921] text-[#e2e8f0] rounded-lg hover:bg-[#2d3748] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors"
            >
              {isCompleting ? 'Complete' : 'Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SessionCompletionModal;
